/**
 * Pipedrive Deals Page
 * List of deals with search, filtering, and pagination
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { usePipedriveDeals, usePipedriveDealFilters } from '@/hooks/use-pipedrive-api'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Briefcase, Search, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react'
import type { PipedriveDeal } from '@/types/pipedrive'
import { SEMANTIC } from '@/lib/theme'

export default function PipedriveDeals() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [stage, setStage] = useState('')
  const [owner, setOwner] = useState('')
  const limit = 25

  const { data, isLoading, error } = usePipedriveDeals({
    page,
    limit,
    search,
    status: status || undefined,
    stage: stage || undefined,
    owner: owner || undefined,
  })

  const { data: filters } = usePipedriveDealFilters()

  const deals = data?.data ?? []
  const total = data?.total ?? 0
  const totalPages = data?.pages ?? 1

  const handleSearch = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  const clearFilters = () => {
    setSearch('')
    setStatus('')
    setStage('')
    setOwner('')
    setPage(1)
  }

  const getStatusStyle = (status: string | null) => {
    switch (status) {
      case 'won':
        // Pistachio fill + Prussian text (DealDetail pattern)
        return { backgroundColor: SEMANTIC.success.fill, color: SEMANTIC.success.text }
      case 'lost':
        // Raspberry fill + white text
        return { backgroundColor: SEMANTIC.error.fill, color: SEMANTIC.error.text }
      case 'open':
        // Navy fill + white text
        return { backgroundColor: SEMANTIC.info.fill, color: SEMANTIC.info.text }
      default:
        // Pale grey fill + Navy text
        return { backgroundColor: SEMANTIC.neutral.fill, color: SEMANTIC.neutral.text }
    }
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Deals</h1>
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
          Failed to load deals. Please try again later.
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Briefcase className="h-6 w-6" aria-hidden="true" />
            Deals
          </h1>
          <p className="text-muted-foreground">{total.toLocaleString()} deals</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search deals..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-9"
                  aria-label="Search deals"
                />
              </div>
            </div>
            <Select
              value={status || '__all__'}
              onValueChange={(v) => {
                setStatus(v === '__all__' ? '' : v)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-[150px]" aria-label="Filter by status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Statuses</SelectItem>
                {filters?.statuses?.map((s) => (
                  <SelectItem key={s} value={s} className="capitalize">
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={stage || '__all__'}
              onValueChange={(v) => {
                setStage(v === '__all__' ? '' : v)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-[180px]" aria-label="Filter by stage">
                <SelectValue placeholder="Stage" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Stages</SelectItem>
                {filters?.stages?.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={owner || '__all__'}
              onValueChange={(v) => {
                setOwner(v === '__all__' ? '' : v)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-[180px]" aria-label="Filter by owner">
                <SelectValue placeholder="Owner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Owners</SelectItem>
                {filters?.owners?.map((o) => (
                  <SelectItem key={o} value={o}>
                    {o}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead className="text-right">Value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton className="h-4 w-40" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-28" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-6 w-16" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-20 ml-auto" />
                    </TableCell>
                  </TableRow>
                ))
              ) : deals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No deals found
                  </TableCell>
                </TableRow>
              ) : (
                deals.map((deal: PipedriveDeal) => (
                  <TableRow key={deal.id}>
                    <TableCell>
                      <Link
                        to={`/pipedrive/deals/${deal.id}`}
                        className="font-medium text-primary hover:underline flex items-center gap-1"
                      >
                        {deal.title}
                        <ExternalLink className="h-3 w-3" aria-hidden="true" />
                      </Link>
                    </TableCell>
                    <TableCell>
                      {deal.organization_id ? (
                        <Link
                          to={`/pipedrive/organizations/${deal.organization_id}`}
                          className="text-primary hover:underline"
                        >
                          {deal.organization || '-'}
                        </Link>
                      ) : (
                        deal.organization || '-'
                      )}
                    </TableCell>
                    <TableCell>{deal.owner || '-'}</TableCell>
                    <TableCell>
                      <Badge style={getStatusStyle(deal.status)} variant="secondary">
                        {deal.status || 'Unknown'}
                      </Badge>
                    </TableCell>
                    <TableCell>{deal.stage_name || '-'}</TableCell>
                    <TableCell className="text-right font-medium">
                      {deal.value ? `$${deal.value.toLocaleString()}` : '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of{' '}
            {total.toLocaleString()}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
